import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Trophy, Medal, Calendar, ChevronDown, ExternalLink, Play } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useSiteImages } from '../context/SiteImagesContext';
import { fetchResults, fetchGalas, resolveImg } from '../api';
import { usePageMeta } from '../hooks/usePageMeta';

const Results = () => {
  usePageMeta({ title: 'Résultats & Galas - GR La Saline', description: 'Résultats de compétitions et galas du club GR La Saline. Palmarès, photos et vidéos.' });
  const siteConfig = useSiteConfig();
  const images = useSiteImages();
  const [resultsData, setResultsData] = useState([]);
  const [galaPhotos, setGalaPhotos] = useState([]);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedGalaYear, setSelectedGalaYear] = useState('');

  useEffect(() => {
    fetchResults().then((data) => {
      const sorted = (data || []).map(y => ({
        ...y,
        competitions: [...(y.competitions || [])].sort((a, b) => {
          const da = a.date || '';
          const db = b.date || '';
          if (da && db) return db.localeCompare(da); // both dated: most recent first
          if (da && !db) return -1;                    // dated before undated
          if (!da && db) return 1;
          return 0;
        })
      }));
      setResultsData(sorted);
      if (sorted.length) setSelectedYear(sorted[0].year);
    }).catch(() => {});
    fetchGalas().then((data) => {
      setGalaPhotos(data || []);
      if (data && data.length) setSelectedGalaYear(data[0].year);
    }).catch(() => {});
  }, []);

  const getMedalColor = (medal) => {
    switch (medal) {
      case 'Or': return 'from-yellow-400 to-yellow-600';
      case 'Argent': return 'from-gray-300 to-gray-500';
      case 'Bronze': return 'from-orange-400 to-orange-600';
      default: return 'from-fuchsia-400 to-pink-400';
    }
  };

  const currentYearResults = resultsData.find(year => year.year === selectedYear);
  const currentGala = galaPhotos.find(gala => gala.year === selectedGalaYear);

  useEffect(() => {
    if (!window.location.hash) return;
    if (!galaPhotos.length && !resultsData.length) return;
    const id = window.location.hash.slice(1);
    const scroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
      else {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    const timers = [300, 900].map((d) => setTimeout(scroll, d));
    return () => timers.forEach(clearTimeout);
  }, [resultsData, galaPhotos]);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.results_hero}
            alt="Résultats"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6"
          >
            Nos Succès
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6"
          >
            <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Résultats</span> & Galas
          </motion.h1>
        </div>
      </section>

      {/* Competition Results */}
      <section className="relative py-24" ref={ref}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Résultats des <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Compétitions</span>
            </h2>
            <p className="text-white/60 text-lg">
              Découvrez les performances exceptionnelles de nos gymnastes sur les compétitions locales, régionales, nationales et même internationales.
            </p>
          </motion.div>

          {/* Year Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center gap-4 mb-16 flex-wrap"
          >
            {resultsData.map((yearData) => (
              <button
                key={yearData.year}
                onClick={() => setSelectedYear(yearData.year)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedYear === yearData.year
                    ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-lg shadow-fuchsia-500/50'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                }`}
              >
                {yearData.year}
              </button>
            ))}
          </motion.div>

          {/* Competition Cards */}
          {currentYearResults && (
            <div className="space-y-8 max-w-6xl mx-auto">
              {currentYearResults.competitions.map((competition, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/30 transition-all duration-500"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{competition.name}</h3>
                      {competition.date && (
                        <div className="flex items-center gap-2 text-white/60">
                          <Calendar size={16} />
                          <span className="text-sm">{new Date(competition.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                    <Trophy className="text-fuchsia-400" size={32} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {competition.results.map((result, idx) => (
                      <motion.div
                        key={idx}
                        whileHover={{ scale: 1.02 }}
                        className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-fuchsia-500/30 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getMedalColor(result.medal)} flex items-center justify-center flex-shrink-0`}>
                            <Medal size={20} className="text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-white truncate">{result.athlete}</h4>
                            <p className="text-xs text-white/60">{result.category}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/60">{result.apparatus}</span>
                          <span className={`font-bold bg-gradient-to-r ${getMedalColor(result.medal)} bg-clip-text text-transparent`}>
                            {result.medal}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {competition.photos && competition.photos.filter(Boolean).length > 0 && (
                    <div className="mt-6 columns-1 sm:columns-2 md:columns-3 gap-4">
                      {competition.photos.filter(Boolean).slice(0, 6).map((photo, pi) => (
                        <motion.div
                          key={pi}
                          whileHover={{ scale: 1.02 }}
                          className="mb-4 break-inside-avoid bg-white/5 rounded-xl overflow-hidden border border-white/10"
                        >
                          <img
                            src={resolveImg(photo)}
                            alt={`${competition.name} - photo ${pi + 1}`}
                            loading="lazy"
                            className="w-full h-auto block"
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gala Section */}
      <section id="galas" className="scroll-mt-24 relative py-24 bg-gradient-to-b from-black via-fuchsia-950/10 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Galas de <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Fin d'Année</span>
            </h2>
            <p className="text-white/60 text-lg">
              Revivez les moments magiques de nos spectacles annuels
            </p>
          </motion.div>

          {/* Year Selector for Gala */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center gap-4 mb-16 flex-wrap"
          >
            {galaPhotos.map((gala) => (
              <button
                key={gala.year}
                onClick={() => setSelectedGalaYear(gala.year)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedGalaYear === gala.year
                    ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-lg shadow-fuchsia-500/50'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                }`}
              >
                Gala {gala.year}
              </button>
            ))}
          </motion.div>

          {/* Gala Content */}
          {currentGala && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-6xl mx-auto"
            >
              <h3 className="text-3xl font-bold text-white text-center mb-12">{currentGala.title}</h3>
              
              {/* Photo Grid - masonry, aspect-preserved, up to 6 */}
              <div className="columns-1 sm:columns-2 md:columns-3 gap-4 mb-12">
                {currentGala.photos.slice(0, 6).map((photo, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    className="mb-4 break-inside-avoid bg-white/5 rounded-xl overflow-hidden border border-white/10"
                  >
                    <img
                      src={resolveImg(photo)}
                      alt={`Gala ${currentGala.year} - Photo ${index + 1}`}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                  </motion.div>
                ))}
              </div>

              {/* YouTube Link */}
              <div className="text-center">
                <motion.a
                  href={siteConfig.socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-500 rounded-full text-white font-bold text-lg shadow-lg shadow-red-500/50 hover:shadow-red-500/70 transition-all duration-300"
                >
                  <Play size={20} className="fill-white" />
                  Voir la vidéo sur YouTube
                  <ExternalLink size={18} />
                </motion.a>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Results;
